import styled from 'styled-components';
import { GlobalToken } from 'antd';

// Styles for MainAssistantPage component

export const MainPageContainer = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  background-color: transparent;
  overflow: hidden;
  position: relative;
  z-index: 1;
`;

export const ScrollableContentWrapper = styled.div<{ theme: { token: GlobalToken } }>`
  flex: 1;
  overflow-x: visible;
  overflow-y: auto;
  min-height: 0;
  max-height: 100%;
  display: flex;
  flex-direction: row;
  position: relative;
  overscroll-behavior: none; /* completely disable native overscroll */
  touch-action: pan-y pinch-zoom; /* allow vertical scrolling and pinch zoom */
  scrollbar-gutter: stable; /* Reserve space for scrollbar to prevent layout shift */

  /* Custom scrollbar styling - thin, discrete, with original color */
  &::-webkit-scrollbar {
    width: 6px !important;
  }

  &::-webkit-scrollbar-track {
    background: transparent !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme: { token } }) => token.colorTextQuaternary} !important;
    border-radius: 3px !important;
    border: none !important;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme: { token } }) => token.colorTextTertiary} !important;
  }

  /* Firefox scrollbar */
  scrollbar-width: thin !important;
  scrollbar-color: ${({ theme: { token } }) => token.colorTextQuaternary} transparent !important;
`;

export const RightPanel = styled.div<{ theme: { token: GlobalToken } }>`
  width: 295px;
  flex-shrink: 0;
  background-color: transparent;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 900px) {
    display: none;
  }
`;

export const ContentSection = styled.div<{ $isInsightsExpanded?: boolean; theme: { token: GlobalToken } }>`
  flex: 1;
  max-width: 1240px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  box-sizing: border-box;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0 ${({ theme: { token } }) => token.paddingLG}px;
  padding-bottom: ${({ $isInsightsExpanded, theme: { token } }) =>
    $isInsightsExpanded ? token.paddingXL * 2 + token.marginXL * 2 : token.paddingXL * 2}px;
  margin-top: ${({ $isInsightsExpanded, theme: { token } }) => ($isInsightsExpanded ? token.marginXL * 2 : 0)}px;
  margin-bottom: ${({ $isInsightsExpanded, theme: { token } }) => ($isInsightsExpanded ? token.marginXL * 2 : 0)}px;
  overflow-x: visible;
  overflow-y: visible;
  min-height: 0;
`;

export const PromptsSection = styled.div<{ theme: { token: GlobalToken } }>`
  margin-bottom: ${({ theme: { token } }) => token.marginXL * 2}px;
  background: transparent;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const SuggestionsWrapper = styled.div<{ $isHidden?: boolean; theme: { token: GlobalToken } }>`
  width: 100%;
  opacity: ${({ $isHidden }) => ($isHidden ? 0 : 1)};
  max-height: ${({ $isHidden }) => ($isHidden ? '0' : 'none')};
  overflow: ${({ $isHidden }) => ($isHidden ? 'hidden' : 'visible')};
  transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    margin-bottom 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: ${({ $isHidden, theme: { token } }) => ($isHidden ? '0' : token.marginXL)}px;
`;

export const InsightsSection = styled.div<{ theme: { token: GlobalToken } }>`
  margin-top: ${({ theme: { token } }) => token.marginXL * 2}px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const InsightsHeader = styled.div<{ theme: { token: GlobalToken } }>`
  margin-bottom: ${({ theme: { token } }) => token.marginLG}px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const InsightsTitleRow = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  gap: ${({ theme: { token } }) => token.marginSM}px;
  flex-wrap: wrap;
`;

export const InsightsTitle = styled.h2<{ theme: { token: GlobalToken } }>`
  color: ${({ theme: { token } }) => token.colorTextLightSolid};
  font-size: ${({ theme: { token } }) => token.fontSizeHeading4 || 20}px;
  font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
  margin: 0;
  line-height: ${({ theme: { token } }) => token.lineHeightHeading4 || 28}px;
`;

export const PendingValue = styled.span<{ theme: { token: GlobalToken } }>`
  color: ${({ theme: { token } }) => token.colorTextLightSolid};
  font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
  line-height: ${({ theme: { token } }) => token.lineHeightLG || 24}px;
`;

export const SectionTitle = styled.h2<{ theme: { token: GlobalToken } }>`
  color: ${({ theme: { token } }) => token.colorTextLightSolid};
  font-size: ${({ theme: { token } }) => token.fontSizeXL}px;
  font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
  margin: 0 0 ${({ theme: { token } }) => token.marginLG}px 0;
  line-height: 1.4;
`;

export const PromptsGrid = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  gap: ${({ theme: { token } }) => token.marginXS}px;
  width: 100%;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const NewConversationCard = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${({ theme: { token } }) => token.padding}px;
  background: #1b2d43;
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 126px;
  width: 220px;
  flex-shrink: 0;
  gap: 2px;
  transform: translateZ(0);
  will-change: transform;

  &:hover {
    transform: translateY(-2px) translateZ(0);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

export const NewConversationIconWrapper = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  height: 47px;
  padding-top: 5px;
  width: 68px;
`;

export const NewConversationIconCircle = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 41.48px;
  height: 30.5px;
`;

export const NewConversationContent = styled.div<{ theme: { token } }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
`;

export const NewConversationText = styled.span<{ theme: { token: GlobalToken } }>`
  color: ${({ theme: { token } }) => token.colorTextBase || token.colorText || '#000000'};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
  line-height: ${({ theme: { token } }) => token.lineHeight}px;
  text-align: left;
`;

export const NewConversationSubtext = styled.span<{ theme: { token: GlobalToken } }>`
  color: ${({ theme: { token } }) => token.colorTextDescription || token.colorTextSecondary || 'rgba(0, 0, 0, 0.45)'};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  line-height: 24px;
  text-align: left;
`;

export const PromptCard = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${({ theme: { token } }) => token.padding}px;
  background: #1b2d43;
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 126px;
  width: 220px;
  flex-shrink: 0;
  gap: 2px;
  transform: translateZ(0);
  will-change: transform;

  &:hover {
    transform: translateY(-2px) translateZ(0);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

export const PromptIconWrapper = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  height: 47px;
  padding-top: 5px;
  width: 68px;
`;

export const StationOverviewIcon = styled.div<{ theme: { token: GlobalToken } }>`
  width: 41.48px;
  height: 30.5px;
  background: url('https://www.figma.com/api/mcp/asset/0559379b-c50e-4e9f-84b4-6bc304033589');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
`;

export const SalesmateBadge = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
  background: #d42a1d;
  border-radius: 3.227px;
  color: #ffffff;
  font-family: 'League Gothic', sans-serif;
  font-size: 19.364px;
  font-weight: 400;
  width: 68px;
  height: 29px;
`;

export const DefaultPromptIcon = styled.div<{ theme: { token: GlobalToken } }>`
  width: 41.48px;
  height: 30.5px;
  background: ${({ theme: { token } }) => token.colorPrimary};
  border-radius: ${({ theme: { token } }) => token.borderRadiusSM}px;
  opacity: 0.3;
`;

export const PromptCardContent = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
`;

export const PromptText = styled.span<{ theme: { token: GlobalToken } }>`
  color: ${({ theme: { token } }) => token.colorTextBase || token.colorText || '#000000'};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
  line-height: ${({ theme: { token } }) => token.lineHeight}px;
  text-align: left;
`;

export const PromptSubtext = styled.span<{ theme: { token: GlobalToken } }>`
  color: ${({ theme: { token } }) => token.colorTextDescription || token.colorTextSecondary || 'rgba(0, 0, 0, 0.45)'};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  line-height: 24px;
  text-align: left;
`;

export const TabsContainer = styled.div<{ theme: { token: GlobalToken } }>`
  .ant-tabs-nav {
    margin-bottom: 0;

    &::before {
      display: none;
    }
  }

  .ant-tabs-nav-wrap {
    justify-content: flex-start !important;
  }

  .ant-tabs-nav-list {
    justify-content: flex-start !important;
    width: 100%;
  }

  .ant-tabs-tab {
    text-align: left;
    justify-content: flex-start;
    color: ${({ theme: { token } }) => token.colorTextSecondary} !important;

    &.ant-tabs-tab-active {
      .ant-tabs-tab-btn {
        color: ${({ theme: { token } }) => token.colorPrimary} !important;
      }
    }
  }

  .ant-tabs-tab-btn {
    text-align: left;
    color: ${({ theme: { token } }) => token.colorTextSecondary} !important;
  }

  .ant-tabs-ink-bar {
    background: ${({ theme: { token } }) => token.colorPrimary} !important;
  }
`;

export const ValueBreakdown = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  gap: ${({ theme: { token } }) => token.marginMD}px;
  margin-bottom: ${({ theme: { token } }) => token.marginLG}px;
  flex-wrap: wrap;
`;

export const ValueBreakdownLabel = styled.span<{ theme: { token: GlobalToken } }>`
  color: ${({ theme: { token } }) => token.colorText};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  line-height: ${({ theme: { token } }) => token.lineHeight}px;
`;

export const ValueBreakdownItems = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  gap: ${({ theme: { token } }) => token.marginLG}px;
  flex-wrap: wrap;
`;

export const ValueBreakdownItem = styled.div<{
  $type: 'risk' | 'maintenance' | 'energy';
  theme: { token: GlobalToken };
}>`
  display: flex;
  align-items: center;
  gap: ${({ theme: { token } }) => token.marginXS}px;
  padding: 0 ${({ theme: { token } }) => token.paddingXS}px;
  height: 28px;
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  background: ${({ $type }) => {
    switch ($type) {
      case 'risk':
        return 'linear-gradient(-55.55deg, rgba(255, 91, 85, 0) 32.7%, rgba(255, 91, 85, 0.16) 98.65%), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%)';
      case 'maintenance':
        return 'linear-gradient(-56.71deg, rgba(255, 131, 25, 0) 45.03%, rgba(255, 131, 25, 0.14) 90.19%), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%)';
      case 'energy':
        return 'linear-gradient(-43.41deg, rgba(82, 196, 26, 0) 36.19%, rgba(82, 196, 26, 0.1) 94.41%), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%)';
      default:
        return 'transparent';
    }
  }};
`;

export const ValueBreakdownLine = styled.div<{
  $type: 'risk' | 'maintenance' | 'energy';
  theme: { token: GlobalToken };
}>`
  width: 0;
  height: 18px;
  border-left: 1px solid
    ${({ $type, theme: { token } }) => {
      switch ($type) {
        case 'risk':
          return token.colorError;
        case 'maintenance':
          return token.colorWarning;
        case 'energy':
          return '#46af16';
        default:
          return token.colorText;
      }
    }};
  transform: rotate(90deg);
`;

export const ValueBreakdownText = styled.span<{
  $type: 'risk' | 'maintenance' | 'energy';
  theme: { token: GlobalToken };
}>`
  color: ${({ $type, theme: { token } }) => {
    switch ($type) {
      case 'risk':
        return token.colorError;
      case 'maintenance':
        return token.colorWarning;
      case 'energy':
        return '#46af16';
      default:
        return token.colorText;
    }
  }};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  line-height: ${({ theme: { token } }) => token.lineHeight}px;
`;

export const ValueBreakdownChip = styled.span<{ theme: { token: GlobalToken } }>`
  background: ${({ theme: { token } }) => token.colorFillQuaternary};
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  padding: ${({ theme: { token } }) => token.paddingXXS}px ${({ theme: { token } }) => token.paddingXS}px;
  color: ${({ theme: { token } }) => token.colorTextLightSolid};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  line-height: ${({ theme: { token } }) => token.lineHeight}px;
  height: 20px;
  display: inline-flex;
  align-items: center;
`;

export const InsightsGrid = styled.div<{ theme: { token: GlobalToken } }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(408px, 1fr));
  gap: ${({ theme: { token } }) => token.marginXS}px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const InsightCardWrapper = styled.div<{ theme: { token: GlobalToken } }>`
  width: 100%;
`;

export const InsightCardSkeleton = styled.div<{ theme: { token: GlobalToken } }>`
  width: 100%;
  background-color: ${({ theme: { token } }) => token.colorBgElevated};
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  overflow: hidden;
`;

export const EmptyState = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme: { token } }) => token.paddingXL * 2}px ${({ theme: { token } }) => token.padding}px;
  text-align: center;
  grid-column: 1 / -1;

  .empty-message {
    color: ${({ theme: { token } }) => token.colorTextSecondary};
    font-size: ${({ theme: { token } }) => token.fontSize}px;
    margin: 0;
  }
`;

export const OverscrollArrow = styled.div<{ $progress: number; theme: { token: GlobalToken } }>`
  position: absolute;
  top: ${({ theme: { token } }) => token.marginLG}px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme: { token } }) => token.colorBgContainer};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  opacity: ${({ $progress }) => Math.min($progress * 2, 1)};
  transition: opacity 0.2s ease, transform 0.1s ease;
  cursor: pointer;
  pointer-events: auto;

  &:hover {
    background: ${({ theme: { token } }) => token.colorFillTertiary};
    transform: translateX(-50%) scale(1.1);
  }

  &:active {
    transform: translateX(-50%) scale(0.95);
  }

  .anticon {
    font-size: 16px;
    color: ${({ theme: { token } }) => token.colorPrimary};
    transition: transform 0.2s ease;
  }
`;
